import React, { createContext, useContext, useMemo } from 'react';
import { useAuthStore } from '../auth/store/authStore';
import { parsePermissionsFromToken } from './parsePermissionsFromToken';

export interface FeaturePermissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

interface PermissionsContextValue {
  permissions: Set<string>;
  hasPermission: (permission: string) => boolean;
  feature: (featureKey: string) => FeaturePermissions;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = useAuthStore((s) => s.token);

  const value = useMemo<PermissionsContextValue>(() => {
    const permissions = parsePermissionsFromToken(token);

    return {
      permissions,
      hasPermission: (permission: string) => permissions.has(permission),
      feature: (featureKey: string) => ({
        canView: permissions.has(`${featureKey}.view`),
        canCreate: permissions.has(`${featureKey}.create`),
        canEdit: permissions.has(`${featureKey}.edit`),
        canDelete: permissions.has(`${featureKey}.delete`),
      }),
    };
  }, [token]);

  return (
    <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
  );
};

export function usePermissions(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return ctx;
}

export function usePermission(permission: string): boolean {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
}

export function useFeature(featureKey: string): FeaturePermissions {
  const { feature } = usePermissions();
  return feature(featureKey);
}
